<template>
  <v-dialog v-model="internalOpen" max-width="520" persistent>
    <v-card class="pa-4">
      <v-card-title class="text-h6 pb-2">
        {{ stepTitle }}
      </v-card-title>

      <v-card-text>
        <p v-if="step === 'account'" class="text-body-2 mb-4 text--secondary">
          {{ accountStepIntro }}
        </p>

        <div v-if="step === 'account'">
          <v-text-field
            v-model="bankBranch"
            label="Agência"
            outlined
            dense
            :disabled="loading"
            hide-details="auto"
            class="mb-3"
          />
          <v-text-field
            v-model="accountNumber"
            label="Conta (com dígito, ex: 00000000-1)"
            outlined
            dense
            :disabled="loading"
            hide-details="auto"
          />
        </div>

        <div v-else-if="step === 'sms'">
          <p class="text-body-2 mb-2 text--secondary">
            Enviamos um código para
            <strong>{{ maskedPhone || "seu telefone cadastrado" }}</strong>.
          </p>
          <v-text-field
            v-model="smsCode"
            label="Código SMS"
            outlined
            dense
            maxlength="8"
            :disabled="loading"
            hide-details="auto"
            autocomplete="one-time-code"
          />
          <v-btn
            text
            small
            color="primary"
            class="mt-2 px-0"
            :disabled="loading || resendCooldown > 0"
            @click="handleResend"
          >
            {{
              resendCooldown > 0
                ? `Reenviar em ${resendCooldown}s`
                : "Reenviar SMS"
            }}
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn text :disabled="loading" @click="handleClose">
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="step === 'account'"
          color="#00A859"
          dark
          :loading="loading"
          :disabled="!canRequestSms"
          @click="handleRequestSms"
        >
          Enviar SMS
        </v-btn>
        <v-btn
          v-else
          color="#00A859"
          dark
          :loading="loading"
          :disabled="!canConfirm"
          @click="handleConfirm"
        >
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { RequestPagBankSms } from "@/services/franchises/RequestPagBankSms";
import { ConfirmPagBankSms } from "@/services/franchises/ConfirmPagBankSms";

const requestPagBankSms = new RequestPagBankSms();
const confirmPagBankSms = new ConfirmPagBankSms();

export default defineComponent({
  name: "PagBankConnectDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    franchiseId: {
      type: String,
      default: "",
    },
    associateId: {
      type: String,
      default: "",
    },
    isFranchiseAdmin: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      step: "account" as "account" | "sms",
      bankBranch: "",
      accountNumber: "",
      smsCode: "",
      maskedPhone: "",
      loading: false,
      resendCooldown: 0,
      resendTimer: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    internalOpen: {
      get(): boolean {
        return this.value;
      },
      set(open: boolean) {
        this.$emit("input", open);
      },
    },
    stepTitle(): string {
      return this.step === "account"
        ? "Conectar conta PagBank"
        : "Confirmar código SMS";
    },
    canRequestSms(): boolean {
      return (
        !!this.connectTargetId &&
        !!this.bankBranch.trim() &&
        !!this.accountNumber.trim() &&
        !this.loading
      );
    },
    connectTargetId(): string {
      if (this.isFranchiseAdmin && this.franchiseId) {
        return this.franchiseId;
      }
      return this.associateId;
    },
    connectPayload():
      | { franchise_id: string }
      | { associate_id: string }
      | null {
      if (this.isFranchiseAdmin && this.franchiseId) {
        return { franchise_id: this.franchiseId };
      }
      if (this.associateId) {
        return { associate_id: this.associateId };
      }
      return null;
    },
    canConfirm(): boolean {
      return !!this.smsCode.trim() && !this.loading;
    },
    accountStepIntro(): string {
      if (this.isFranchiseAdmin) {
        return "Informe os dados da conta PagBank da franquia. Enviaremos um SMS para o telefone cadastrado nessa conta.";
      }
      return "Informe os dados da sua conta PagBank. Enviaremos um SMS para o telefone cadastrado nessa conta.";
    },
  },
  watch: {
    value(open: boolean) {
      if (!open) {
        this.resetState();
      }
    },
  },
  beforeDestroy() {
    this.clearResendTimer();
  },
  methods: {
    resetState() {
      this.step = "account";
      this.bankBranch = "";
      this.accountNumber = "";
      this.smsCode = "";
      this.maskedPhone = "";
      this.loading = false;
      this.resendCooldown = 0;
      this.clearResendTimer();
    },
    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },
    startResendCooldown(seconds: number) {
      this.clearResendTimer();
      this.resendCooldown = seconds;
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown <= 1) {
          this.resendCooldown = 0;
          this.clearResendTimer();
          return;
        }
        this.resendCooldown -= 1;
      }, 1000);
    },
    handleClose() {
      this.internalOpen = false;
    },
    async handleRequestSms() {
      if (!this.connectPayload) {
        return;
      }

      this.loading = true;
      try {
        const response = await requestPagBankSms.execute({
          bank_branch: this.bankBranch.trim(),
          account_number: this.accountNumber.trim(),
          ...this.connectPayload,
        });
        this.maskedPhone = response.phone_number;
        this.step = "sms";
        this.startResendCooldown(response.retry_after_seconds || 60);
        this.$store.dispatch("showSnack", {
          message: "SMS enviado com sucesso.",
          color: "green",
        });
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error_messages?.[0]?.description ||
          "Não foi possível enviar o SMS. Verifique os dados da conta.";
        this.$store.dispatch("showSnack", { message, color: "red" });
      } finally {
        this.loading = false;
      }
    },
    async handleResend() {
      await this.handleRequestSms();
    },
    async handleConfirm() {
      if (!this.connectPayload) {
        return;
      }

      this.loading = true;
      try {
        await confirmPagBankSms.execute({
          sms_code: this.smsCode.trim(),
          ...this.connectPayload,
        });
        this.$emit("connected");
        this.handleClose();
        this.$store.dispatch("showSnack", {
          message: "Conta PagBank conectada com sucesso.",
          color: "green",
        });
      } catch (error: any) {
        const status = error?.response?.status;
        let message =
          error?.response?.data?.message ||
          "Não foi possível confirmar o código SMS.";

        if (status === 410) {
          message =
            "A sessão de confirmação expirou. Solicite um novo SMS.";
          this.step = "account";
        }

        this.$store.dispatch("showSnack", { message, color: "red" });
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>
